import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import {
    Box, Paper, Typography, TextField, Button, Grid, Table, TableBody,
    TableCell, TableHead, TableRow, Divider, Alert,
    InputAdornment, CircularProgress, Autocomplete, TableContainer,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Replay as ReplayIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ProjectChooseDialog from '../components/subcontract/ProjectChooseDialog';
import ActionStatusDialog, { ActionStatusDialogState } from '../components/common/ActionStatusDialog';
import { SubcontractProjectOption, useSubcontractProjects } from '../hooks/useSubcontractProjects';

interface SubcontractSummary {
    subcontractid: number;
    subcontractnumber: string;
    contractnumber?: string;
    vendorname: string;
    subcontractvalue: number;
    contractvalue?: number;
    contractstartdate: string;
    contractcompletiondate: string;
    projectcode: string;
    status: string;
    [key: string]: any;
}

interface WorkDoneRecord {
    workdoneid: number;
    periodstart: string;
    periodend: string;
    percentagecomplete: number;
    cumulativeworkdone: number;
    sapgrpodocentry?: number;
    sapgrpodocnum?: number;
    sappoststatus?: string;
    remarks: string | null;
    createdbyuserid: number;
    createdbyname?: string;
    createddate: string;
    [key: string]: any;
}

const SubcontractWorkDone: React.FC = () => {
    const dispatch = useDispatch();
    const { companyId: selectedCompanyId, projects, loading: loadingProjects } = useSubcontractProjects();

    // Search / select state
    const [selectedProject, setSelectedProject] = useState<SubcontractProjectOption | null>(null);
    const [projectPickerOpen, setProjectPickerOpen] = useState(false);
    const [subcontracts, setSubcontracts] = useState<SubcontractSummary[]>([]);
    const [selectedSubcontract, setSelectedSubcontract] = useState<SubcontractSummary | null>(null);
    const [loadingSC, setLoadingSC] = useState(false);

    // Loaded data
    const [scSummary, setScSummary] = useState<SubcontractSummary | null>(null);
    const [approvedVariationsTotal, setApprovedVariationsTotal] = useState(0);
    const [workDoneHistory, setWorkDoneHistory] = useState<WorkDoneRecord[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    // New work done form
    const [periodDate, setPeriodDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [cumulativeWorkDone, setCumulativeWorkDone] = useState<string>('');
    const [wdDescription, setWdDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [repostingId, setRepostingId] = useState<number | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<WorkDoneRecord | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [actionStatus, setActionStatus] = useState<ActionStatusDialogState>({
        open: false,
        status: 'processing',
        title: '',
        message: '',
    });

    // Computed values
    const originalValue = Number(scSummary?.contractvalue ?? scSummary?.subcontractvalue ?? 0);
    const totalValue = originalValue + approvedVariationsTotal;
    const latestPostedWorkDone = workDoneHistory.find((row) =>
        String(row.sappoststatus || '').toUpperCase() === 'POSTED'
        || Number(row.sapgrpodocentry || 0) > 0
    );
    const lastCumulative = latestPostedWorkDone
        ? Number(latestPostedWorkDone.cumulativeworkdone || 0)
        : 0;

    // When project changes, load subcontracts for that project
    useEffect(() => {
        if (!selectedProject) {
            setSubcontracts([]);
            setSelectedSubcontract(null);
            return;
        }
        const loadSC = async () => {
            setLoadingSC(true);
            try {
                const params: any = { projectCode: selectedProject.code };
                if (selectedCompanyId) params.companyId = selectedCompanyId;
                const res = await apiService.listSubcontracts(params);
                const list = res?.data || res || [];
                setSubcontracts(Array.isArray(list) ? list : []);
            } catch (err: any) {
                dispatch(showSnackbar({ message: 'Failed to load subcontracts', severity: 'error' }));
            } finally {
                setLoadingSC(false);
            }
        };
        loadSC();
    }, [selectedProject, selectedCompanyId, dispatch]);

    // Load subcontract detail and work done history
    const handleLoad = useCallback(async () => {
        if (!selectedSubcontract) {
            dispatch(showSnackbar({ message: 'Please select a subcontract', severity: 'warning' }));
            return;
        }
        setLoadingData(true);
        try {
            const [scRes, wdRes] = await Promise.all([
                apiService.getSubcontract(selectedSubcontract.subcontractid),
                apiService.getWorkDone(selectedSubcontract.subcontractid),
            ]);

            const sc = scRes?.data || scRes;
            setScSummary(sc);
            setApprovedVariationsTotal(Number(sc?.totalvariations || 0));

            // Work done history, sorted descending by date
            const wdList = wdRes?.data || wdRes || [];
            const sorted: WorkDoneRecord[] = (Array.isArray(wdList) ? wdList : []).sort(
                (a: WorkDoneRecord, b: WorkDoneRecord) =>
                    new Date(b.periodend || b.perioddate || b.createddate).getTime() - new Date(a.periodend || a.perioddate || a.createddate).getTime()
            );
            setWorkDoneHistory(sorted);
        } catch (err: any) {
            const detail = err?.response?.data?.message || err.message || 'Failed to load subcontract data';
            dispatch(showSnackbar({ message: detail, severity: 'error' }));
        } finally {
            setLoadingData(false);
        }
    }, [selectedSubcontract, dispatch]);

    // Save new work done record
    const handleSave = async () => {
        if (!scSummary) return;

        if (!periodDate) {
            dispatch(showSnackbar({ message: 'Period date is required', severity: 'warning' }));
            return;
        }

        const numValue = parseFloat(cumulativeWorkDone);
        if (isNaN(numValue) || numValue < 0) {
            dispatch(showSnackbar({ message: 'Please enter a valid cumulative work done value', severity: 'warning' }));
            return;
        }

        // Validation: must be >= last cumulative
        if (numValue < lastCumulative) {
            dispatch(showSnackbar({
                message: `Cumulative work done (${formatNumber(numValue)}) cannot be less than last recorded value (${formatNumber(lastCumulative)})`,
                severity: 'error',
            }));
            return;
        }

        // Validation: must be <= total value
        if (numValue > totalValue) {
            dispatch(showSnackbar({
                message: `Cumulative work done (${formatNumber(numValue)}) cannot exceed total subcontract value (${formatNumber(totalValue)})`,
                severity: 'error',
            }));
            return;
        }

        setSaving(true);
        setActionStatus({
            open: true,
            status: 'processing',
            title: 'Submitting Work Done',
            message: 'Saving work done and creating the SAP GRPO.',
            details: `${scSummary.subcontractnumber || scSummary.contractnumber || ''} | Cumulative ${formatNumber(numValue)} KWD`,
        });
        try {
            const payload: any = {
                periodstart: periodDate,
                periodend: periodDate,
                percentagecomplete: totalValue > 0 ? ((numValue / totalValue) * 100).toFixed(2) : 0,
                cumulativeworkdone: numValue,
            };
            if (wdDescription.trim()) payload.remarks = wdDescription.trim();

            const response = await apiService.createWorkDone(scSummary.subcontractid, payload);
            const saved = response?.data || response || {};
            const grpoDocNum = saved?.sapgrpodocnum || saved?.sapPosting?.grpoDocNum;
            const grpoDocEntry = saved?.sapgrpodocentry || saved?.sapPosting?.grpoDocEntry;
            setActionStatus({
                open: true,
                status: 'success',
                title: 'Work Done Submitted',
                message: grpoDocNum || grpoDocEntry
                    ? 'Work done was saved and the SAP GRPO was created successfully.'
                    : 'Work done was recorded successfully.',
                details: grpoDocNum || grpoDocEntry
                    ? `GRPO DocNum ${String(grpoDocNum || '-')} / DocEntry ${String(grpoDocEntry || '-')}`
                    : undefined,
            });

            // Reset form
            setCumulativeWorkDone('');
            setWdDescription('');
            setPeriodDate(new Date().toISOString().split('T')[0]);

            // Reload work done history
            const wdRes = await apiService.getWorkDone(scSummary.subcontractid);
            const wdList = wdRes?.data || wdRes || [];
            const sorted: WorkDoneRecord[] = (Array.isArray(wdList) ? wdList : []).sort(
                (a: WorkDoneRecord, b: WorkDoneRecord) =>
                    new Date(b.periodend || b.perioddate || b.createddate).getTime() - new Date(a.periodend || a.perioddate || a.createddate).getTime()
            );
            setWorkDoneHistory(sorted);
        } catch (err: any) {
            const message = err?.response?.data?.message || err.message || 'Failed to save work done';
            setActionStatus({
                open: true,
                status: 'error',
                title: 'Work Done Submission Failed',
                message,
                details: err?.response?.status ? `HTTP status ${err.response.status}` : undefined,
            });
        } finally {
            setSaving(false);
        }
    };

    const reloadWorkDoneHistory = async () => {
        if (!scSummary) return;
        const wdRes = await apiService.getWorkDone(scSummary.subcontractid);
        const wdList = wdRes?.data || wdRes || [];
        const sorted: WorkDoneRecord[] = (Array.isArray(wdList) ? wdList : []).sort(
            (a: WorkDoneRecord, b: WorkDoneRecord) =>
                new Date(b.periodend || b.perioddate || b.createddate).getTime() - new Date(a.periodend || a.perioddate || a.createddate).getTime()
        );
        setWorkDoneHistory(sorted);
    };

    const handleRepost = async (wd: WorkDoneRecord) => {
        if (!scSummary) return;
        setRepostingId(wd.workdoneid);
        try {
            const response: any = await apiService.post(
                `/subcontract/${scSummary.subcontractid}/work-done/${wd.workdoneid}/repost`
            );
            const respData = response?.data || response || {};
            const grpoDocNum = respData?.sapgrpodocnum || respData?.sapPosting?.grpoDocNum;
            const grpoDocEntry = respData?.sapgrpodocentry || respData?.sapPosting?.grpoDocEntry;
            dispatch(showSnackbar({
                message: grpoDocNum || grpoDocEntry
                    ? `Reposted to SAP successfully. DocNum ${String(grpoDocNum || '')} / DocEntry ${String(grpoDocEntry || '')}`
                    : 'Reposted to SAP successfully',
                severity: 'success',
            }));
            await reloadWorkDoneHistory();
        } catch (err: any) {
            dispatch(showSnackbar({
                message: err?.response?.data?.message || err.message || 'Failed to repost to SAP',
                severity: 'error',
            }));
        } finally {
            setRepostingId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!scSummary || !deleteConfirm) return;
        setDeleting(true);
        try {
            await apiService.delete(
                `/subcontract/${scSummary.subcontractid}/work-done/${deleteConfirm.workdoneid}`
            );
            dispatch(showSnackbar({ message: 'Failed work done entry deleted', severity: 'success' }));
            setDeleteConfirm(null);
            await reloadWorkDoneHistory();
        } catch (err: any) {
            dispatch(showSnackbar({
                message: err?.response?.data?.message || err.message || 'Failed to delete work done entry',
                severity: 'error',
            }));
        } finally {
            setDeleting(false);
        }
    };

    const formatNumber = (val: number) =>
        val.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB');
        } catch {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        } catch {
            return dateStr;
        }
    };

    return (
        <Box component="section" aria-labelledby="swd-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography
                component="h1"
                id="swd-heading"
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, fontSize: { xs: 18, sm: 20 } }}
            >
                Subcontract Work Done
            </Typography>

            {/* Search / Select Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Select Subcontract
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            label="Project Number"
                            value={selectedProject ? `${selectedProject.code} - ${selectedProject.name}` : ''}
                            onClick={() => setProjectPickerOpen(true)}
                            placeholder="Choose project..."
                            InputProps={{
                                readOnly: true,
                                endAdornment: loadingProjects
                                    ? <CircularProgress size={18} />
                                    : <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            size="small"
                            options={subcontracts}
                            loading={loadingSC}
                            getOptionLabel={(opt) => {
                                const subcontractNumber = String(opt.subcontractnumber || '').trim();
                                const contractNumber = String(opt.contractnumber || '').trim();
                                const contractSuffix = contractNumber && contractNumber !== subcontractNumber ? ` (${contractNumber})` : '';
                                return `${subcontractNumber}${contractSuffix} - ${opt.vendorname || ''}`;
                            }}
                            value={selectedSubcontract}
                            onChange={(_e, val) => {
                                setSelectedSubcontract(val);
                                setScSummary(null);
                                setWorkDoneHistory([]);
                                setApprovedVariationsTotal(0);
                            }}
                            isOptionEqualToValue={(opt, val) => opt.subcontractid === val.subcontractid}
                            disabled={!selectedProject}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subcontract Number"
                                    placeholder="Search subcontract..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingSC && <CircularProgress size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={loadingData ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                            onClick={handleLoad}
                            disabled={!selectedSubcontract || loadingData}
                            sx={{ bgcolor: '#F0AB00', '&:hover': { bgcolor: '#F2BB2E' }, color: '#000' }}
                        >
                            Load
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Subcontract Summary */}
            {scSummary && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Subcontract Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="SC Number"
                                value={scSummary.subcontractnumber || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Contract Number"
                                value={String(scSummary.contractnumber || '').trim() !== String(scSummary.subcontractnumber || '').trim() ? (scSummary.contractnumber || '') : ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Vendor"
                                value={scSummary.vendorname || ''}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Original Value (KWD)"
                                value={formatNumber(originalValue)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Approved Variations (KWD)"
                                value={formatNumber(approvedVariationsTotal)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                size="small"
                                label="Total Value (KWD)"
                                value={formatNumber(totalValue)}
                                InputProps={{ readOnly: true }}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-input': { fontWeight: 700 },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Work Done History Table */}
            {scSummary && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Work Done History
                    </Typography>
                    {workDoneHistory.length === 0 ? (
                        <Alert severity="info" sx={{ mb: 1 }}>No work done records for this subcontract.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Cumulative Work Done (KWD)</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Periodic Work Done (KWD)</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>SAP GRPO</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {workDoneHistory.map((wd, idx) => {
                                        const cumCurrent = Number(wd.cumulativeworkdone || 0);
                                        // History is sorted descending, so the previous entry is the next index
                                        const cumPrevious = idx < workDoneHistory.length - 1
                                            ? Number(workDoneHistory[idx + 1].cumulativeworkdone || 0)
                                            : 0;
                                        const periodic = cumCurrent - cumPrevious;
                                        const isFailed = wd.sappoststatus === 'Failed';
                                        return (
                                        <TableRow key={wd.workdoneid} hover sx={isFailed ? { backgroundColor: '#fff3f3' } : undefined}>
                                            <TableCell>{formatDate(wd.periodend || wd.perioddate)}</TableCell>
                                            <TableCell align="right">{formatNumber(cumCurrent)}</TableCell>
                                            <TableCell align="right">{formatNumber(periodic)}</TableCell>
                                            <TableCell>
                                                {(wd.sapgrpodocnum || wd.sapgrpodocentry)
                                                    ? `DocNum ${wd.sapgrpodocnum || '-'} / DocEntry ${wd.sapgrpodocentry || '-'}`
                                                    : isFailed
                                                        ? <Typography variant="body2" color="error" component="span" sx={{ fontWeight: 600 }}>Failed</Typography>
                                                        : '-'}
                                            </TableCell>
                                            <TableCell>{wd.remarks || wd.description || '-'}</TableCell>
                                            <TableCell>{wd.createdbyname || '-'}</TableCell>
                                            <TableCell>{formatDateTime(wd.createddate)}</TableCell>
                                            <TableCell align="center">
                                                {isFailed ? (
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        <Tooltip title="Repost to SAP">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleRepost(wd)}
                                                                disabled={repostingId === wd.workdoneid}
                                                            >
                                                                {repostingId === wd.workdoneid ? <CircularProgress size={18} /> : <ReplayIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete failed entry">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => setDeleteConfirm(wd)}
                                                                disabled={repostingId === wd.workdoneid}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Record New Work Done Form */}
            {scSummary && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Record New Work Done
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {lastCumulative > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Last recorded cumulative work done: <strong>{formatNumber(lastCumulative)} KWD</strong>.
                            New value must be between {formatNumber(lastCumulative)} and {formatNumber(totalValue)} KWD.
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                size="small"
                                label="Period Date"
                                type="date"
                                value={periodDate}
                                onChange={(e) => setPeriodDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                size="small"
                                label="Cumulative Work Done (KWD)"
                                type="number"
                                value={cumulativeWorkDone}
                                onChange={(e) => setCumulativeWorkDone(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">KWD</InputAdornment>,
                                }}
                                inputProps={{ step: '0.001', min: lastCumulative, max: totalValue }}
                                helperText={`Min: ${formatNumber(lastCumulative)} | Max: ${formatNumber(totalValue)}`}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                size="small"
                                label="Description"
                                value={wdDescription}
                                onChange={(e) => setWdDescription(e.target.value)}
                                multiline
                                minRows={2}
                                maxRows={4}
                                helperText="Optional"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                            onClick={handleSave}
                            disabled={saving || !periodDate || !cumulativeWorkDone}
                            sx={{ bgcolor: '#F0AB00', '&:hover': { bgcolor: '#F2BB2E' }, color: '#000' }}
                        >
                            Save Work Done
                        </Button>
                    </Box>
                </Paper>
            )}
            <ProjectChooseDialog
                open={projectPickerOpen}
                projects={projects}
                loading={loadingProjects}
                selectedCode={selectedProject?.code}
                onClose={() => setProjectPickerOpen(false)}
                onSelect={(project) => {
                    setSelectedProject(project);
                    setSelectedSubcontract(null);
                    setScSummary(null);
                    setWorkDoneHistory([]);
                    setApprovedVariationsTotal(0);
                }}
            />
            <ActionStatusDialog
                {...actionStatus}
                onClose={() => setActionStatus((current) => ({ ...current, open: false }))}
            />
            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)}>
                <DialogTitle>Delete Failed Work Done Entry</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this failed work done entry?
                        {deleteConfirm && (
                            <>
                                <br /><br />
                                <strong>Date:</strong> {formatDate(deleteConfirm.periodend || (deleteConfirm as any).perioddate)}<br />
                                <strong>Cumulative:</strong> {formatNumber(Number(deleteConfirm.cumulativeworkdone || 0))} KWD<br />
                                <strong>Error:</strong> {(deleteConfirm as any).sapposterror || 'Unknown'}
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubcontractWorkDone;
